import React from 'react';
import { Archive, RotateCcw, Trash2, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { noteColors, getText } from './constants';

export const ArchiveDialog = ({
  isOpen,
  onOpenChange,
  language,
  archivedNotes,
  onRestore,
  onDelete
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby="archive-dialog-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            {getText('archivedNotes', language)}
          </DialogTitle>
          <DialogDescription id="archive-dialog-description">
            {language === 'de' 
              ? 'Hier findest du alle erledigten Notizen. Du kannst sie wiederherstellen oder endgültig löschen.'
              : 'Here you can find all completed notes. You can restore or permanently delete them.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[400px] overflow-y-auto">
          {archivedNotes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Archive className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>{getText('noArchived', language)}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {archivedNotes.map((note) => {
                const colorStyle = noteColors[note.color] || noteColors.yellow;
                return (
                  <div
                    key={note.id}
                    className={`p-3 rounded-lg ${colorStyle.bg} ${colorStyle.border} border flex items-start justify-between gap-3`}
                    data-testid={`archived-note-${note.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      {note.category && (
                        <div className="flex items-center gap-1 mb-1">
                          <Tag className="h-3 w-3 text-slate-500" />
                          <span className="text-xs font-medium text-slate-500 uppercase">
                            {note.category}
                          </span>
                        </div>
                      )}
                      {note.image && (
                        <img src={note.image} alt="" className="w-16 h-12 object-cover rounded mb-2" />
                      )}
                      <p className="text-sm text-slate-800 line-through opacity-70">{note.content}</p>
                      {note.completed_at && (
                        <p className="text-xs text-slate-500 mt-1">
                          {language === 'de' ? 'Erledigt am' : 'Completed'}: {new Date(note.completed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRestore(note.id)}
                        className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-100"
                        title={getText('restore', language)}
                        data-testid={`restore-note-${note.id}`}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(note.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-100"
                        title={getText('delete', language)}
                        data-testid={`delete-archived-note-${note.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ArchiveDialog;
